'use client';

import React, { useEffect, useState } from 'react';

const creditData = [
  { label: 'Credits transferred in full', pct: 34, color: 'var(--sumi)' },
  { label: 'Partial transfer (50–90%)', pct: 41, color: 'var(--tatami)' },
  { label: 'Pending / disputed', pct: 14, color: 'rgba(26,26,26,0.3)' },
  { label: 'No transfer approved', pct: 11, color: 'var(--vermillion)' },
];

const universities = [
  { name: 'Yonsei University', country: '🇰🇷', rate: 78, tier: 'High' },
  { name: 'TU Berlin', country: '🇩🇪', rate: 91, tier: 'Excellent' },
  { name: 'Sciences Po Paris', country: '🇫🇷', rate: 65, tier: 'Moderate' },
  { name: 'University of Amsterdam', country: '🇳🇱', rate: 84, tier: 'High' },
  { name: 'Waseda University', country: '🇯🇵', rate: 71, tier: 'Moderate' },
];

export default function CreditPanel() {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="scroll-panel" style={{ background: 'var(--washi)' }}>
      <div className="noise-overlay" />
      <div className="w-full h-full flex flex-col md:flex-row items-stretch pt-16 pb-12">

        {/* LEFT */}
        <div
          className="flex-shrink-0 md:w-[42%] flex flex-col justify-between px-8 md:px-12 py-8 md:py-10"
          style={{ borderRight: '1px solid rgba(184,175,166,0.4)' }}
        >
          <div className="panel-content">
            <div className="section-label mb-4">Chapter 03 — Credits</div>
            <hr className="tatami-rule mb-8" />

            <h2
              className="font-fraunces font-medium mb-6"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', color: 'var(--sumi)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              Only{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--vermillion)' }}>34%</span>{' '}
              of students<br />
              transferred all credits.
            </h2>

            <p className="font-dm-sans mb-8" style={{ fontSize: '14px', color: 'var(--sumi-light)', lineHeight: 1.7, fontWeight: 300, maxWidth: '320px' }}>
              The Learning Agreement is not a guarantee. It's a starting point for a negotiation most students don't know they're in.
            </p>

            {/* Donut-style breakdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {creditData?.map((item, i) => (
                <div key={item?.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: item?.color, flexShrink: 0 }} />
                  <span className="font-dm-sans" style={{ fontSize: '13px', color: 'var(--sumi)', flex: 1, fontWeight: 400 }}>{item?.label}</span>
                  <span className="font-plex-mono" style={{ fontSize: '14px', fontWeight: 500, color: item?.color === 'var(--vermillion)' ? 'var(--vermillion)' : 'var(--sumi)' }}>
                    {item?.pct}%
                  </span>
                </div>
              ))}
            </div>

            {/* Handwritten note */}
            <div style={{ marginTop: '24px', background: '#fffef9', border: '1px solid rgba(184,175,166,0.3)', padding: '12px 16px', transform: 'rotate(0.8deg)' }}>
              <p className="font-fraunces" style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--sumi)', lineHeight: 1.6 }}>
                "I came back to find my home university didn't recognize 3 of my 5 courses. I had to take them again."
              </p>
              <div className="section-label mt-2" style={{ color: 'var(--vermillion)' }}>— Amara D., Accra → Paris</div>
            </div>

            {/* Quiet CTA — appears after panel 3 as specified */}
            <div style={{ marginTop: '28px' }}>
              <p className="font-dm-sans" style={{ fontSize: '13px', color: 'var(--sumi-light)', lineHeight: 1.6 }}>
                The guide covers the exact Learning Agreement language that protects you.{' '}
                <a
                  href="#"
                  className="vermillion-link font-fraunces"
                  style={{ fontSize: '13px', fontStyle: 'italic' }}
                >
                  Read the Full Guide →
                </a>
              </p>
            </div>
          </div>

          <div className="section-label mt-4" style={{ color: 'var(--tatami)' }}>
            Survey: 2,841 returned exchange students · 2024
          </div>
        </div>

        {/* RIGHT: University credit transfer rates */}
        <div className="flex-1 flex flex-col justify-center px-8 md:px-12 py-8 md:py-10">
          <div className="panel-content">
            <div className="section-label mb-6">Credit transfer success rate by partner university</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {universities?.map((uni, i) => (
                <div key={uni?.name}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '16px' }}>{uni?.country}</span>
                      <span className="font-dm-sans" style={{ fontSize: '13px', fontWeight: 500, color: 'var(--sumi)' }}>{uni?.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span
                        className="font-plex-mono"
                        style={{
                          fontSize: '9px',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: uni?.tier === 'Excellent' ? 'var(--washi)' : uni?.tier === 'High' ? 'var(--washi)' : 'var(--sumi)',
                          background: uni?.tier === 'Excellent' ? 'var(--sumi)' : uni?.tier === 'High' ? 'var(--tatami)' : 'rgba(184,175,166,0.3)',
                          padding: '2px 6px',
                        }}
                      >
                        {uni?.tier}
                      </span>
                      <span className="font-fraunces" style={{ fontSize: '20px', fontWeight: 600, color: 'var(--sumi)', letterSpacing: '-0.02em' }}>
                        {uni?.rate}%
                      </span>
                    </div>
                  </div>
                  <div style={{ height: '4px', background: 'rgba(184,175,166,0.3)' }}>
                    <div style={{
                      height: '100%',
                      background: uni?.rate > 85 ? 'var(--sumi)' : uni?.rate > 75 ? 'var(--tatami)' : 'rgba(26,26,26,0.4)',
                      width: animated ? `${uni?.rate}%` : '0%',
                      transition: `width 0.9s cubic-bezier(0.19, 1, 0.22, 1) ${i * 0.1}s`,
                    }} />
                  </div>
                </div>
              ))}
            </div>

            <hr className="tatami-rule mt-8 mb-4" />

            {/* Stacked bar legend for overall breakdown */}
            <div>
              <div className="section-label mb-3">Overall breakdown — all programs surveyed</div>
              <div style={{ display: 'flex', height: '20px', overflow: 'hidden', borderRadius: '1px' }}>
                {creditData?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      width: animated ? `${item?.pct}%` : '0%',
                      background: item?.color,
                      transition: `width 1s cubic-bezier(0.19, 1, 0.22, 1) ${i * 0.15}s`,
                      position: 'relative',
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                {creditData?.map(item => (
                  <div key={item?.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '8px', height: '8px', background: item?.color }} />
                    <span className="section-label">{item?.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}